import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setBooking } from '../features/booking/bookingSlice';
import { setGuestList } from '../features/guests/guestsSlice';
import { setIsLoading } from '../app/store/majesticSlice';
import * as Sentry from '@sentry/react';
import {
  getGenderByInt,
  getStringDate,
  getDocumentTypeByInt,
  getGenderShortByInt,
  generateCustomId,
} from '../utils';

const basiAuthorization = 'Basic cHJlY2hlY2tpbi11c2VyOm5STXh5RSZzSTEyMA==';

// Enviar logs al servidor
const sendLogToServer = async (log) => {
  // Obtener el JWT desde localStorage o del estado de Redux
  // const token = localStorage.getItem('token');

  // Verificar si el token existe
  /* if (!token) {
    console.error('No JWT token available, cannot send log');
    return;
  } */

  try {
    await fetch(`${import.meta.env.VITE_APP_API_BIOMETRICS_MAJESTIC}/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(log),
    });
  } catch (error) {
    // TODO: Enviar error a sentry
    console.error('Error enviando logs:', error);
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_APP_API_MAJESTIC_RESORTS}/api-gateway`,
  prepareHeaders: (headers, { getState }) => {
    headers.set('Authorization', basiAuthorization);
    return headers;
  },
});

const baseQueryWithLogger = async (args, contextApi, extraOptions) => {
  const params = {
    ...args,
    url: `${import.meta.env.VITE_APP_API_MAJESTIC_RESORTS}/api-gateway${args.url}`,
  };
  const timestamp = new Date().toISOString();
  const { booking } = contextApi.getState();
  let reservationNumber = '';
  if (contextApi.endpoint === 'getBooking') {
    reservationNumber = args.body.ReservationNumber;
  } else {
    const { booking: dataBooking } = booking;
    reservationNumber = dataBooking.NumReserva;
  }

  // Registro de log de la solicitud
  const requestLog = {
    type: 'REQUEST',
    timestamp,
    action: `${contextApi.endpoint}-pending`,
    status: 'pending',
    params,
    bookingNumber: reservationNumber,
  };

  Sentry.logger.info(
    Sentry.logger
      .fmt`[${reservationNumber}] Get reserva/huespedes request.`,
    {
      NumReserva: reservationNumber,
      params: params,
    }
  )
  await sendLogToServer(requestLog);

  const result = await baseQuery(args, contextApi, extraOptions);

  const responseLog = {
    type: 'RESPONSE',
    timestamp,
    action: `${contextApi.endpoint}-fulfilled`,
    status: 'fulfilled',
    bookingNumber: reservationNumber,
    result: result.data,
  };

  Sentry.logger.info(
    Sentry.logger
      .fmt`[${reservationNumber}] Get reserva/huespedes response.`,
    {
      NumReserva: reservationNumber,
      params: params,
      result: result.data,
    }
  )
  sendLogToServer(responseLog);

  return result;
};

const handlerErrorResponse = (error) => {
  const dataError = {
    status: error.status,
    message: 'Error desconocido',
    description: error,
  };
  if (error) {
    if (error?.status === 400 && error?.data?.error) {
      dataError.message = error.data.error;
    } // else if (error?.status === 404) {
    //   return <div>Error: No se encontraron posts.</div>;
    // } else if (error?.status === 500) {
    //   return <div>Error: Problema en el servidor. Inténtalo de nuevo más tarde.</div>;
    // } else {
    //   return <div>Error desconocido: {error?.status}</div>;
    // }
  }

  return dataError;
};

export const majesticResortsApi = createApi({
  reducerPath: 'majesticResortsApi',
  baseQuery: baseQueryWithLogger,
  endpoints: (builder) => ({
    getBooking: builder.mutation({
      query: (params) => ({
        url: '/reservas/lista',
        method: 'post',
        body: params,
      }),
      // Transformar el error
      transformErrorResponse: handlerErrorResponse,
      transformResponse: (response) => {
        // Transforma la respuesta
        if (response?.result) {
          if (response.result === 'KO') {
            if (response?.errors) {
              // Lanza un error personalizado
              throw new Error(response.errors.join(''));
            }
          }
        }

        let booking = {};
        if (response?.reservations) {
          [booking] = response.reservations;
        }

        return booking;
      },
      // Usa onQueryStarted para manejar la logica adicional usando reducers
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          // Registro de log de la solicitud
          // const requestLog = {
          //   type: 'REQUEST',
          //   timestamp: new Date().toISOString(),
          //   action: 'getBooking',
          //   status: 'pending',
          //   params: arg,
          //   bookingNumber: arg.ReservationNumber,
          // };

          // Enviar log de solicitud
          // sendLogToServer(requestLog);

          // Accion de Redux para guardar los datos
          dispatch(setIsLoading(true));
          const { data } = await queryFulfilled; // Espera a que la mutacion se complete

          // Guarda los datos en el estado de Redux
          dispatch(setBooking(data));
          dispatch(setIsLoading(false));

          // const numReserva = getState().booking?.booking?.NumReserva ?? 'XXXXXX';

          // Registro de log de la respuesta
          // const responseLog = {
          //   type: 'RESPONSE',
          //   timestamp: new Date().toISOString(),
          //   action: 'getBooking',
          //   status: 'fulfilled',
          //   bookingNumber: numReserva,
          //   data,
          // };

          // Enviar log de respuesta
          // sendLogToServer(responseLog);
        } catch (error) {
          dispatch(setIsLoading(false));
          console.error('Error en la mutacion getBooking:', error);

          // Log de error
          const errorLog = {
            type: 'ERROR',
            timestamp: new Date().toISOString(),
            action: 'getBooking-rejected',
            status: 'rejected',
            params: arg,
            error,
            bookingNumber: arg.ReservationNumber,
          };

          // Enviar log de error
          Sentry.logger.error(
            Sentry.logger
              .fmt`[${arg.ReservationNumber}] Error al obtener la reserva.`,
            {
              NumReserva: arg.ReservationNumber,
              params: arg,
              error: error,
            }
          )
          sendLogToServer(errorLog);
        }
      },
    }),

    getGuests: builder.mutation({
      query: (params) => ({
        url: '/huespedes/listar',
        method: 'post',
        body: params,
      }),
      transformErrorResponse: handlerErrorResponse,
      transformResponse: (response) => {
        // Transforma la respuesta
        if (response?.result) {
          if (response.result === 'KO') {
            if (response?.errors) {
              // Lanza un error personalizado
              throw new Error(response.errors.join(''));
            }
          }
        }

        let guests = [];
        if (response?.results) {
          guests = response.results.sort(
            (a, b) => Number(a.NumeroCliente) - Number(b.NumeroCliente)
          );
          // guests = response.results;
          guests = guests.map((guest, index) => {
            const item = {
              ...guest,
              // customId: Math.random().toString(10).slice(10),
              customId: generateCustomId(),
              customNumber: index + 1,
              FormatterFechaCaducidad: getStringDate(guest.FechaCaducidad, 'YYYYMMDD'),
              FormatterFechaEntrada: getStringDate(guest.FechaEntrada, 'YYYYMMDD'),
              FormatterFechaExpedicion: getStringDate(guest.FechaExpedicion, 'YYYYMMDD'),
              FormatterFechaNacimiento: getStringDate(guest.FechaNacimiento, 'YYYYMMDD'),
              FormatterFechaSalida: getStringDate(guest.FechaSalida, 'YYYYMMDD'),
              FormatterSexo: getGenderByInt(guest.Sexo),
              FormatterSexoShort: getGenderShortByInt(guest.Sexo),
              FormatterTipoDocumento: getDocumentTypeByInt(guest.TipoDocumento),
            };
            return item;
          });
        }

        return guests;
      },
      // Usa onQueryStarted para manejar la logica adicional usando reducers
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          // const requestLog = {
          //   type: 'REQUEST',
          //   timestamp: new Date().toISOString(),
          //   action: 'getGuests',
          //   status: 'pending',
          //   params: arg,
          //   bookingNumber: arg.reservationNumber,
          // };

          // Enviar log de solicitud
          // sendLogToServer(requestLog);

          // Accion de Redux para guardar los datos
          dispatch(setIsLoading(true));
          const { data } = await queryFulfilled; // Espera a que la mutacion se complete
          // console.log('Datos recibidos:', data);
          // Guarda los datos en el estado de Redux
          dispatch(setGuestList(data));
          dispatch(setIsLoading(false));

          // const numReserva = getState().booking?.booking?.NumReserva ?? 'XXXXXX';
          // const responseLog = {
          //   type: 'RESPONSE',
          //   timestamp: new Date().toISOString(),
          //   action: 'getGuests',
          //   status: 'fulfilled',
          //   bookingNumber: numReserva,
          //   data,
          // };

          // Enviar log de respuesta
          // sendLogToServer(responseLog);
        } catch (error) {
          dispatch(setIsLoading(false));
          console.error('Error en la mutacion getGuests:', error);

          const errorLog = {
            type: 'ERROR',
            timestamp: new Date().toISOString(),
            action: 'getGuests-rejected',
            status: 'rejected',
            params: arg,
            error,
            bookingNumber: arg.reservationNumber,
          };

          // Enviar log de error
          Sentry.logger.error(
            Sentry.logger
              .fmt`[${arg.reservationNumber}] Error al obtener los huespedes.`,
            {
              NumReserva: arg.reservationNumber,
              params: arg,
              error: error,
            }
          )
          sendLogToServer(errorLog);
        }
      },
    }),

    createGuest: builder.mutation({
      query: (params) => ({
        url: '/huespedes/crear',
        method: 'post',
        body: params,
      }),
      transformErrorResponse: handlerErrorResponse,
      transformResponse: (response) => {
        // Transforma la respuesta
        if (response?.result) {
          if (response.result === 'KO') {
            if (response?.errors) {
              // Lanza un error personalizado
              throw new Error(response.errors.join(''));
            }
          }
        }
        return response;
      },
      // Usa onQueryStarted para manejar la logica adicional usando reducers
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          // const requestLog = {
          //   type: 'REQUEST',
          //   timestamp: new Date().toISOString(),
          //   action: 'createGuest',
          //   status: 'pending',
          //   params: arg,
          //   bookingNumber: arg.reservationNumber,
          // };

          // Enviar log de solicitud
          // sendLogToServer(requestLog);

          dispatch(setIsLoading(true));
          await queryFulfilled;
          dispatch(setIsLoading(false));

          // const numReserva = getState().booking?.booking?.NumReserva ?? 'XXXXXX';
          // const responseLog = {
          //   type: 'RESPONSE',
          //   timestamp: new Date().toISOString(),
          //   action: 'createGuest',
          //   status: 'fulfilled',
          //   bookingNumber: numReserva,
          // };

          // Enviar log de respuesta
          // sendLogToServer(responseLog);
        } catch (error) {
          dispatch(setIsLoading(false));
          console.error('Error en la mutacion createGuest:', error);

          const errorLog = {
            type: 'ERROR',
            timestamp: new Date().toISOString(),
            action: 'createGuests-rejected',
            status: 'rejected',
            params: arg,
            bookingNumber: arg?.d?.NumReserva,
            error,
          };

          // Enviar log de error
          Sentry.logger.error(
            Sentry.logger
              .fmt`[${arg?.d?.NumReserva}] Error al crear huesped.`,
            {
              NumReserva: arg?.d?.NumReserva,
              params: arg,
              error: error,
            }
          )
          sendLogToServer(errorLog);
        }
      },
    }),

    updateGuest: builder.mutation({
      query: (params) => ({
        url: '/huespedes/modificar',
        method: 'post',
        body: params,
      }),
      transformErrorResponse: handlerErrorResponse,
      transformResponse: (response) => {
        // Transforma la respuesta
        if (response?.result) {
          if (response.result === 'KO') {
            if (response?.errors) {
              // Lanza un error personalizado
              throw new Error(response.errors.join(''));
            }
          }
        }

        return response;
      },
      // Usa onQueryStarted para manejar la logica adicional usando reducers
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          // const requestLog = {
          //   type: 'REQUEST',
          //   timestamp: new Date().toISOString(),
          //   action: 'updateGuest',
          //   status: 'pending',
          //   params: arg,
          //   bookingNumber: arg.reservationNumber,
          // };

          // Enviar log de solicitud
          // sendLogToServer(requestLog);

          dispatch(setIsLoading(true));
          await queryFulfilled;
          dispatch(setIsLoading(false));

          // const numReserva = getState().booking?.booking?.NumReserva ?? 'XXXXXX';
          // const responseLog = {
          //   type: 'RESPONSE',
          //   timestamp: new Date().toISOString(),
          //   action: 'updateGuest',
          //   status: 'fulfilled',
          //   bookingNumber: numReserva,
          // };

          // Enviar log de respuesta
          // sendLogToServer(responseLog);
        } catch (error) {
          dispatch(setIsLoading(false));
          console.error('Error en la mutacion updateGuest:', error);

          const errorLog = {
            type: 'ERROR',
            timestamp: new Date().toISOString(),
            action: 'updateGuests-rejected',
            status: 'rejected',
            params: arg,
            bookingNumber: arg?.d?.NumReserva,
            error,
          };

          // Enviar log de error
          Sentry.logger.error(
            Sentry.logger
              .fmt`[${arg?.d?.NumReserva}] Error la modificar el huesped.`,
            {
              NumReserva: arg?.d?.NumReserva,
              params: arg,
              error: error,
            }
          )
          sendLogToServer(errorLog);
        }
      },
    }),

    updateBooking: builder.mutation({
      query: (params) => ({
        url: '/reservas/modificar',
        method: 'post',
        body: params,
      }),
      transformErrorResponse: handlerErrorResponse,
      transformResponse: (response) => {
        // Transforma la respuesta
        if (response?.result) {
          if (response.result === 'KO') {
            if (response?.errors) {
              // Lanza un error personalizado
              throw new Error(response.errors.join(''));
            }
          }
        }

        return response;
      },
      // Usa onQueryStarted para manejar la logica adicional usando reducers
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          dispatch(setIsLoading(true));
          await queryFulfilled;
          dispatch(setIsLoading(false));

          // const numReserva = getState().booking?.booking?.NumReserva ?? 'XXXXXX';
          // const responseLog = {
          //   type: 'RESPONSE',
          //   timestamp: new Date().toISOString(),
          //   action: 'updateGuest',
          //   status: 'fulfilled',
          //   bookingNumber: numReserva,
          // };

          // Enviar log de respuesta
          // sendLogToServer(responseLog);
        } catch (error) {
          console.log(arg)
          dispatch(setIsLoading(false));
          console.error('Error en la mutacion updateGuest:', error);

          const errorLog = {
            type: 'ERROR',
            timestamp: new Date().toISOString(),
            action: 'updateBooking-rejected',
            status: 'rejected',
            params: arg,
            bookingNumber: arg?.d?.NumReserva,
            error,
          };

          // Enviar log de error
          Sentry.logger.error(
            Sentry.logger
              .fmt`[${arg?.d?.NumReserva}] Error al modificar la reserva.`,
            {
              NumReserva: arg?.d?.NumReserva,
              params: arg,
              error: error,
            }
          )
          sendLogToServer(errorLog);
        }
      },
    }),
  }),
});

// console.log(majesticResortsApi)
export const {
  useGetBookingMutation,
  useGetGuestsMutation,
  useCreateGuestMutation,
  useUpdateGuestMutation,
  useUpdateBookingMutation,
} = majesticResortsApi;
